import {
  AddBusinessOutlined,
  Close,
  ContactPhoneOutlined,
  Group,
  PersonAddAltOutlined,
  AssignmentIndOutlined,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MenuIcon from "@mui/icons-material/Menu";
import { IconButton, List } from "@mui/material";
import { useMemo, useContext, useState, useCallback } from "react";

import { DASHBOARD_KEYS, defaultSearchPath } from "../../../../globalConstants";
import { inject } from "../../../../lib/itn-ui-kit/services/service-container";
import { setUIError } from "../../../../state/actions";
import HNSColors from "../../../components/ui library/HNSColors";
import DataContext from "../../../context/DataContext";
import { EventsDispatchContext } from "../../../context/EventsProvider";
import useDashboardAnalytics from "../../../hooks/useDashboardAnalytics";
import { useHistoryNavigate } from "../../../hooks/useHistoryNavigate";
import { SvgEnum } from "../../ui library/components/svgs/standard/svg";

import { MainNavBarEnum } from "./MainNavBarEnum";
import SideBarList from "./SideBarList";

export default function SideBarMain({ activeRoute }) {
  // Services
  const currentUserService = inject("current-user-service");
  const landingService = inject("landing-service");
  const { navigationHistoryService } = useHistoryNavigate();
  // Contexts
  const { selectedClient } = useContext(DataContext);
  const eventDispatch = useContext(EventsDispatchContext);
  const [sideBarIsOpen, setSideBarIsOpen] = useState(false);
  const [closeDropdowns, setCloseDropdowns] = useState(false);
  // Redirect paths
  // Search
  const historicSearchPath = navigationHistoryService.get(DASHBOARD_KEYS.search);
  const searchInStoragePath = localStorage.getItem("Search");
  const dashboardDefault = historicSearchPath || defaultSearchPath;
  const caseSearchUrl = useMemo(
    () => searchInStoragePath || dashboardDefault,
    [searchInStoragePath, dashboardDefault]
  );
  const analytics = useDashboardAnalytics();

  const KPIDashboardURL = landingService.PriorityAccessibleRoute;

  const BUTTON_TYPE = {
    DEFAULT: "DEFAULT",
    DROPDOWN: "DROPDOWN",
  };

  const getButtonType = (type) => {
    switch (type) {
      case MainNavBarEnum.USER_MANAGER:
      case MainNavBarEnum.DASHBOARD:
        return BUTTON_TYPE.DROPDOWN;
      default:
        return BUTTON_TYPE.DEFAULT;
    }
  };

  const triggerDropdown = useCallback(() => {
    setCloseDropdowns(true);
    setTimeout(() => setCloseDropdowns(false), 50);
  }, []);

  const generateUserMenuData = () => {
    const userMenuArr = [];

    if (currentUserService?.userRoles.permissions.includes("users.add_user")) {
      userMenuArr.push({
        icon: <PersonAddAltOutlined className="text-black" />,
        title: "Add User",
        order: 1,
        url: "/add-application-user",
      });
    }
    if (currentUserService?.userRoles.permissions.includes("users.view_user")) {
      userMenuArr.push(
        {
          icon: <Group className="text-black" />,
          title: "View/Manage Users",
          order: 2,
          url: "/user-profiles",
        },
        {
          icon: <ContactPhoneOutlined className="text-black" />,
          title: "Contacts",
          order: 4,
          url: "/contact",
        }
      );
    }
    if (currentUserService?.userRoles.permissions.includes("provider.change_provider")) {
      userMenuArr.push({
        icon: <AssignmentIndOutlined className="text-black" />,
        title: "View/Manage Providers",
        order: 3,
        url: "/providers",
      });
    }
    if (currentUserService?.userRoles.permissions.includes("lawyer.add_lawfirm")) {
      userMenuArr.push({
        icon: <AddBusinessOutlined className="text-black" />,
        title: "Add Law Firm",
        order: 5,
        url: "/create-law-firm",
      });
    }
    return userMenuArr.sort((a, b) => a.order - b.order);
  };

  const renderIcon = () => {
    if (sideBarIsOpen) {
      return <Close style={{ fontSize: "40px" }} />;
    }
    return <MenuIcon style={{ fontSize: "40px" }} />;
  };

  const renderCloseIcon = () => {
    if (!sideBarIsOpen) {
      return null;
    }
    return <Close className="self-center text-[30px] md:text-[40px]" />;
  };

  const handlerNavItemFn = useCallback(() => {
    triggerDropdown();
    return setUIError(eventDispatch, {
      uiError: true,
      uiErrorMessage: "Please Select a Client to View Client's Details...",
    });
  }, [triggerDropdown, eventDispatch]);

  const addNavItem = (menuArr, name) => {
    switch (name) {
      case "dashboard":
        if (currentUserService?.userRoles.permissions.includes("casemanager.view_case")) {
          const menuItems =
            analytics?.map((item) => ({
              key: MainNavBarEnum.DASHBOARD_ANALYTICS + item.id,
              title: item.name,
              url: `/analytics/${item.id}`,
            })) ?? [];

          if (/^\/dashboard\/\d+/.test(KPIDashboardURL)) {
            menuItems.push({
              key: MainNavBarEnum.KPI_DASHBOARD,
              title: "KPI Dashboard",
              url: KPIDashboardURL,
            });
          }
          menuArr.push({
            key: MainNavBarEnum.DASHBOARD,
            title: "Dashboards",
            icon: SvgEnum.HisDashboard,
            order: 1,
            type: getButtonType(MainNavBarEnum.DASHBOARD),
            menuItems,
          });
        }
        break;

      case "search-clients":
        menuArr.push({
          key: MainNavBarEnum.SEARCH_CLIENTS,
          title: "Case Search",
          url: caseSearchUrl,
          icon: SvgEnum.HisFolderSearch,
          order: 2,
          type: getButtonType(MainNavBarEnum.SEARCH_CLIENTS),
        });
        break;

      case "provider-map":
        if (
          currentUserService?.userRoles.permissions.includes("provider.add_providerclientstatus")
        ) {
          menuArr.push({
            key: MainNavBarEnum.PROVIDER_MAP,
            title: "Provider Map",
            url: "/provider-map",
            icon: SvgEnum.HisMapMarker,
            order: 4,
            type: getButtonType(MainNavBarEnum.PROVIDER_MAP),
          });
        }
        break;

      case "client-detail":
        if (
          currentUserService?.userRoles.permissions.includes("casemanager.view_case") &
          selectedClient
        ) {
          menuArr.push({
            key: MainNavBarEnum.CLIENT_DETAIL,
            title: "View Last Client",
            url: `/client-detail/${selectedClient}`,
            icon: SvgEnum.HisContact,
            order: 7,
            type: getButtonType(MainNavBarEnum.CLIENT_DETAIL),
            handlerFn: !selectedClient ? handlerNavItemFn : undefined,
          });
        }
        break;

      case "reports":
        menuArr.push({
          key: MainNavBarEnum.REPORTS,
          title: "Reports",
          url: "/reports/list",
          icon: SvgEnum.HisChart,
          order: 8,
          type: getButtonType(MainNavBarEnum.REPORTS),
        });
        break;

      case "user-manager":
        if (currentUserService?.userRoles.permissions.includes("users.view_user")) {
          menuArr.push({
            key: MainNavBarEnum.USER_MANAGER,
            title: "User Management",
            url: "",
            icon: SvgEnum.HisPeople,
            order: 10,
            type: getButtonType(MainNavBarEnum.USER_MANAGER),
            id: "user-manager-menu-popup",
            menuItems: generateUserMenuData(),
          });
        }
        break;

      default:
        throw new Error(`${name} is not a valid option in 'addNavItem'`);
    }
  };

  const generateMenuData = () => {
    const menuArr = [];

    addNavItem(menuArr, MainNavBarEnum.DASHBOARD);
    addNavItem(menuArr, MainNavBarEnum.SEARCH_CLIENTS);
    addNavItem(menuArr, MainNavBarEnum.PROVIDER_MAP);
    addNavItem(menuArr, MainNavBarEnum.CLIENT_DETAIL);
    addNavItem(menuArr, MainNavBarEnum.REPORTS);
    addNavItem(menuArr, MainNavBarEnum.USER_MANAGER);

    return menuArr;
  };

  const Menus = !currentUserService?.userRoles.permissions ? [] : generateMenuData();

  return (
    <>
      <div className="fixed z-10 flex w-full items-center justify-between bg-white shadow-lg sm:hidden">
        <div className="w-1/3">
          <IconButton
            className="block w-[100px] pl-2"
            onClick={() => setSideBarIsOpen(!sideBarIsOpen)}
          >
            {renderIcon()}
          </IconButton>
        </div>
        <div className="w-1/3">&nbsp;</div>
      </div>
      <div className="fixed z-50 flex bg-white shadow-lg">
        <IconButton
          className="absolute z-10 m-4 block pl-2 sm:hidden"
          onClick={() => setSideBarIsOpen(!sideBarIsOpen)}
        >
          {renderCloseIcon()}
        </IconButton>
        <div
          className={`w-0 ${sideBarIsOpen ? "sm:w-64" : "sm:w-16"} relative h-screen bg-white duration-300`}
        >
          <div
            className={`flex-col items-center sm:flex ${
              sideBarIsOpen
                ? "h-[100vh] w-[100vw] overflow-y-auto border-r bg-white sm:w-64"
                : "hidden"
            }`}
          >
            <List
              className={`flex h-full w-full flex-col items-center gap-1 bg-white p-0 [&>li]:p-0`}
            >
              {sideBarIsOpen ? (
                <div className="flex w-full items-center justify-start">
                  <ArrowBackIcon
                    sx={{
                      color: HNSColors.primary[600],
                      "&:hover": {
                        backgroundColor: HNSColors.tertiary[900],
                      },
                    }}
                    className="mx-4 my-4 cursor-pointer rounded-full p-0.5 text-3xl"
                    onClick={() => {
                      setSideBarIsOpen(false);
                      triggerDropdown();
                    }}
                  />
                </div>
              ) : (
                <div className="flex w-full items-center justify-start">
                  <ArrowForwardIcon
                    className="mx-4 my-4 cursor-pointer rounded-full p-0.5 text-3xl text-blue-500 hover:bg-blue-100"
                    onClick={() => {
                      setSideBarIsOpen(true);
                      triggerDropdown();
                    }}
                  />
                </div>
              )}
              {[...Menus]
                .sort((a, b) => a.order - b.order)
                .map((Menu, index) => {
                  return (
                    <SideBarList
                      key={Menu.key}
                      BUTTON_TYPE={BUTTON_TYPE}
                      Menu={Menu}
                      index={index}
                      activeRoute={activeRoute}
                      sideBarIsOpen={sideBarIsOpen}
                      setSideBarIsOpen={setSideBarIsOpen}
                      closeDropdowns={closeDropdowns}
                      triggerDropdown={triggerDropdown}
                    />
                  );
                })}
            </List>
          </div>
        </div>
      </div>
    </>
  );
}
